import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          console.error('Error reading from localStorage:', error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          console.error('Error writing to localStorage:', error);
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing from localStorage:', error);
        }
      },
    },
  },
});

// ✅ معالجة أخطاء تحديث التوكن
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    console.log('✅ تم تحديث التوكن بنجاح');
  } else if (event === 'SIGNED_OUT') {
    console.log('🔓 تم تسجيل الخروج');
    // تنظيف البيانات المحلية
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    localStorage.removeItem('admin_session_v1');
    localStorage.removeItem('investor_token');
    localStorage.removeItem('investor_data');
    localStorage.removeItem('investor_session_v1');
  } else if (event === 'USER_UPDATED') {
    console.log('✅ تم تحديث بيانات المستخدم');
  }
});

// ✅ دالة تنظيف وتعقيم المدخلات
function sanitizeInput(input: string): string {
  return input.trim().replace(/[<>\"']/g, '');
}

// ✅ دالة التحقق من صحة البريد الإلكتروني
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// ✅ تسجيل دخول الأدمن مع حماية محسّنة
export async function signInAdmin({ email, password }: { email: string; password: string }) {
  try {
    // تنظيف الجلسات القديمة أولاً
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.log('تنظيف الجلسة القديمة...');
    }

    // تنظيف المدخلات
    const normalizedEmail = sanitizeInput(email).toLowerCase();
    
    // التحقق من صحة البريد الإلكتروني
    if (!isValidEmail(normalizedEmail)) {
      return { success: false, error: 'البريد الإلكتروني غير صالح' };
    }

    // التحقق من طول كلمة المرور
    if (!password || password.length < 6) {
      return { success: false, error: 'كلمة المرور قصيرة جداً' };
    }

    console.log('🔐 Admin sign-in started');
    console.log('📧 Email:', normalizedEmail);

    // 1) تسجيل الدخول عبر Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (authError || !authData?.user) {
      console.error('❌ Auth signInWithPassword failed:', authError);
      return { success: false, error: 'بيانات الدخول غير صحيحة' };
    }

    const authUserId = authData.user.id;
    console.log('✅ Auth OK. userId:', authUserId);

    // 2) التحقق من وجود المستخدم في جدول admins
    const { data: admin, error: adminError } = await supabase
      .from('admins')
      .select('id, email, full_name, role, is_active, auth_user_id')
      .eq('auth_user_id', authUserId)
      .eq('is_active', true)
      .maybeSingle();

    if (adminError) {
      console.error('❌ Admin lookup failed:', adminError);
      await supabase.auth.signOut();
      return { success: false, error: 'خطأ في التحقق من صلاحيات الإدارة' };
    }

    if (!admin) {
      console.warn('❌ Not an admin. Signing out...');
      await supabase.auth.signOut();
      return { success: false, error: 'ليس لديك صلاحيات دخول الإدارة' };
    }

    console.log('✅ Admin OK:', admin.email, admin.role);

    // 3) حفظ الجلسة بشكل آمن
    const sessionData = {
      auth_user_id: authUserId,
      admin_id: admin.id,
      email: admin.email,
      full_name: admin.full_name,
      role: admin.role,
      loggedInAt: new Date().toISOString(),
    };

    localStorage.setItem('admin_session_v1', JSON.stringify(sessionData));
    localStorage.setItem('admin_token', authData.session?.access_token || '');
    localStorage.setItem('admin_data', JSON.stringify(admin));

    return {
      success: true,
      admin,
      auth_user_id: authUserId,
    };
  } catch (error: any) {
    console.error('💥 Unexpected error in signInAdmin:', error);
    return { success: false, error: 'حدث خطأ غير متوقع' };
  }
}

// ✅ التحقق من صلاحية الجلسة
export async function verifyAdminSession(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('خطأ في جلب الجلسة:', error);
      // إذا كان الخطأ متعلق بـ Refresh Token، نظف البيانات
      if (error.message?.includes('Refresh Token')) {
        await cleanupSession();
      }
      return false;
    }
    
    if (!session) {
      return false;
    }

    // التحقق من انتهاء صلاحية الجلسة
    const expiresAt = session.expires_at;
    if (expiresAt && expiresAt * 1000 < Date.now()) {
      await supabase.auth.signOut();
      await cleanupSession();
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error verifying session:', error);
    await cleanupSession();
    return false;
  }
}

// ✅ دالة تنظيف الجلسة
async function cleanupSession() {
  try {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    localStorage.removeItem('admin_session_v1');
    localStorage.removeItem('investor_token');
    localStorage.removeItem('investor_data');
    localStorage.removeItem('investor_session_v1');
    
    // تنظيف جلسة Supabase
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('خطأ في تسجيل الخروج:', error);
    }
  } catch (error) {
    console.error('خطأ في تنظيف الجلسة:', error);
  }
}